<template>
    <JetDialogModal :show="showModal" @close="closeModal" max-width="2xl">
        <template #title>
            <span v-if="currentPackage">Modification du colis: N°{{ currentPackage.code }}</span>
            <span v-else-if="request">
                Traiter la demande de: {{ request.user ? request.user.name : request.requester_name }}
            </span>
            <span v-else>Ajouter un nouveau colis</span>
        </template>

        <template #content>
            <div v-if="! currentPackage && ! request">
                <div>
                    <ul class="text-sm font-medium flex flex-nowrap overflow-x-scroll no-scrollbar">
                        <li class="pb-3 mr-6 last:mr-0 last:pr-4 sm:last:pr-6 lg:last:pr-8">
                            <a class="whitespace-nowrap cursor-pointer uppercase" @click.prevent="currentTab = 1"
                                :class="currentTab === 1 ? 'text-sky hover:text-sky-400 font-bold' : 'text-gray-500 hover:text-gray-600'"
                            >Client existant
                            </a>
                        </li>
                        <li class="pb-3 mr-6 last:mr-0 first:pl-4 sm:first:pl-6 lg:first:pl-8 last:pr-4 sm:last:pr-6 lg:last:pr-8">
                            <a class="whitespace-nowrap cursor-pointer uppercase" @click.prevent="currentTab = 2"
                                :class="currentTab === 2 ? 'text-sky hover:text-sky-400 font-bold' : 'text-gray-500 hover:text-gray-600'"
                            >Nouveau Client
                            </a>
                        </li>
                    </ul>
                </div>

                <transition
                    enter-active-class="transition ease-out duration-200 transform"
                    enter-from-class="opacity-0 -translate-y-2"
                    enter-to-class="opacity-100 translate-y-0"
                    leave-active-class="transition ease-out duration-200"
                    leave-from-class="opacity-100"
                    leave-to-class="opacity-0"
                >
                    <div class="mb-4">
                        <hr>
                        <h2 class="text-md text-white font-semibold bg-sky px-4">
                            Informations du {{ currentTab === 2 ? 'nouveau' : '' }} client {{ currentTab === 1 ? 'existant' : '' }}
                        </h2>
                        <hr>
                        <div v-show="currentTab === 1">
                            <div class="grid grid-cols-2 gap-4 mt-2">
                                <div class="col-span-2 sm:col-span-2">
                                    <JetLabelRequired for="sender_id" value="Client" />
                                    <Multiselect
                                        id="sender_id"
                                        :delay="0"
                                        :minChars="1"
                                        :searchable="true"
                                        :valueProp="'id'"
                                        :trackBy="'id'"
                                        :filterResults="false"
                                        :resolveOnLoad="true"
                                        v-model="form.sender_id"
                                        placeholder="Tapez pour rechercher le client"
                                        :options="async function(query) {
                                            return await fetchUser(query)
                                        }"
                                    >
                                        <template v-slot:singlelabel="{ value }">
                                            <div class="multiselect-single-label text-sky">
                                                {{ value.name + (value.email ? ' | ' + value.email : '') + (value.full_phone ? ' | ' + value.full_phone : '') }}
                                            </div>
                                        </template>

                                        <template v-slot:option="{ option }">
                                            {{ option.name + (option.email ? ' | ' + option.email : '') + (option.full_phone ? ' | ' + option.full_phone : '') }}
                                        </template>
                                    </Multiselect>
                                    <JetInputError :message="form.errors.sender_id" />
                                </div>
                            </div>
                        </div>
                        <div v-show="currentTab === 2">
                            <div class="grid grid-cols-2 gap-4 mt-2">
                                <div class="col-span-2">
                                    <JetLabelRequired for="sender_name" value="Nom du client" />
                                    <JetInput id="sender_name" type="text" autocomplete="off"
                                        v-model="form.sender_name" placeholder="Entrez le nom du Client"
                                    />
                                    <JetInputError :message="form.errors.sender_name" />
                                </div>
                                <div class="col-span-1">
                                    <JetLabel for="sender_phone" value="Téléphone du client:" />
                                    <vue-tel-input required @input="senderPhoneInput"
                                        :defaultCountry="form.sender_country_code"
                                        :value="form.sender_phone"
                                    />
                                    <JetInputError :message="form.errors.phone" />
                                </div>
                                <div class="col-span-1">
                                    <JetLabel for="sender_email" value="Email du client:" />
                                    <JetInput id="sender_email" type="text" autocomplete="off"
                                        v-model="form.sender_email" placeholder="Entrez l'email du client"
                                    />
                                    <JetInputError :message="form.errors.sender_email" />
                                </div>
                            </div>
                        </div>
                    </div>
                </transition>
                <hr>
            </div>

            <div v-if="request" class="mb-4">
                <h2 class="text-md text-white font-semibold bg-sky px-4 py-1">Informations du client</h2>
                <hr>
                <div class="grid grid-cols-2 gap-4 mt-2">
                    <div class="col-span-2">
                        <JetLabel for="sender_name" value="Nom du client:" />
                        <div v-if="request.user" class="form-input">
                            {{ request.user.name }}
                        </div>
                        <JetInput v-else id="sender_name" type="text" autocomplete="off"
                            v-model="form.sender_name" placeholder="Entrez le nom du client"
                        />
                    </div>
                    <div class="col-span-1">
                        <JetLabel for="sender_email" value="Adresse mail du client:" />
                        <div v-if="request.user" class="form-input">
                            {{ request.user.email ? request.user.email : 'Adresse mail non renseignée' }}
                        </div>
                        <JetInput v-else id="sender_email" type="text" autocomplete="off"
                            v-model="form.sender_email" placeholder="Entrez l'adresse mail du Client"
                        />
                        <JetInputError :message="form.errors.sender_email" />
                    </div>
                    <div class="col-span-1">
                        <JetLabel for="sender_phone" value="Téléphone du client:" />
                        <div v-if="request.user" class="form-input">
                            {{ request.user.full_phone }}
                        </div>
                        <vue-tel-input v-else required @input="senderPhoneInput"
                            :defaultCountry="form.sender_country_code"
                            :value="form.sender_phone"
                        />
                        <JetInputError :message="form.errors.phone" />
                    </div>
                </div>
            </div>

            <h2 class="text-md text-white font-semibold bg-sky px-4 py-1">Informations du colis</h2>

            <div v-if="currentPackage && currentPackage.has_payments" class="border-l-4 bg-red-100 border-red-700 rounded-b text-red-700 p-1.5 shadow-md animate-pulse">
                <div class="flex items-center">
                    <i class="fad fa-exclamation-circle text-4xl text-red-700 mr-4"></i>
                    <div>
                        <p class="font-bold text-red-700">Attention</p>
                        <div class="block text-xs font-semibold">
                            Ce colis possède déjà des paiements enregistrés.
                            La modification du montant peut entraîner des incohérences avec les paiements déjà effectués.
                            Si vous validez la modification du montant du colis, tous les paiements associés seront supprimés.
                        </div>
                    </div>
                </div>
            </div>

            <!-- Code colis (manuel pour combler les trous, sinon auto) -->
            <div class="grid grid-cols-2 gap-4 mt-2">
                <div class="col-span-2">
                    <JetLabel for="code" :value="currentPackage ? 'Code du colis:' : 'Code du colis (Optionnel):'" />
                    <JetInput id="code" type="text" autocomplete="off" v-model="form.code"
                        :placeholder="currentPackage ? 'Entrez le code du colis' : 'Laisser vide pour générer automatiquement (Ex: A-0123)'"
                    />
                    <p v-if="! currentPackage" class="text-small text-gray-400 mt-.5">
                        Saisir un code manuellement uniquement pour réutiliser un numéro manquant.
                    </p>
                    <JetInputError :message="form.errors.code" />
                </div>
            </div>

            <div class="grid grid-cols-2 gap-4 mt-2">
                <div class="col-span-1">
                    <JetLabel for="receiver_name" value="Nom du destinataire:" />
                    <JetInput id="receiver_name" type="text" autocomplete="off"
                        v-model="form.receiver_name" placeholder="Entrez le nom du destinataire"
                    />
                    <JetInputError :message="form.errors.receiver_name" />
                </div>
                <div class="col-span-1">
                    <JetLabel for="receiver_phone" value="Téléphone du destinataire:" />
                    <vue-tel-input required @input="receiverPhoneInput"
                        :defaultCountry="form.receiver_country_code"
                        :value="form.receiver_phone"
                    />
                    <JetInputError :message="form.errors.receiver_phone" />
                </div>
            </div>

            <div class="grid grid-cols-2 gap-4 mt-2">
                <div class="col-span-1">
                    <JetLabel for="weight" value="Nombre de colis:" />
                    <JetInput id="weight" type="number" min="0" placeholder="0"
                        class="text-right" v-model="form.weight" autocomplete="off"
                    />
                    <JetInputError :message="form.errors.weight" />
                </div>
                <div class="col-span-1">
                    <JetLabelRequired for="amount" :value="'Montant du colis en '+currency.symbol" />
                    <JetInput id="amount" type="number" min="0" placeholder="0"
                        class="text-right" v-model="form.amount" autocomplete="off"
                    />
                    <JetInputError :message="form.errors.amount" />
                </div>
            </div>

            <div class="grid grid-cols-2 gap-4 mt-2">
                <div class="col-span-1">
                    <JetLabel for="discount_percent" value="Remise (%):" />
                    <JetInput id="discount_percent" type="number" min="0" max="100" step="0.01" placeholder="0"
                        class="text-right" v-model="form.discount_percent" autocomplete="off"
                    />
                    <p class="text-small text-gray-400 mt-.5">Retard, colis perdu ou endommagé.</p>
                    <JetInputError :message="form.errors.discount_percent" />
                </div>
                <div class="col-span-1">
                    <JetLabel :value="'Montant final en '+currency.symbol+':'" />
                    <div class="form-input w-full bg-gray-50 text-right font-bold text-greendack-700">
                        {{ formatAmount(finalAmount, currency) }}
                    </div>
                    <p v-if="Number(form.discount_percent) > 0" class="text-small text-gray-400 mt-.5">
                        Remise: {{ formatAmount(discountAmount, currency) }}
                    </p>
                </div>
            </div>

            <div v-if="! currentPackage" class="grid grid-cols-2 gap-4 my-2">
                <div class="col-span-1">
                    <JetLabel for="deposit" :value="'Acompte versé en '+currency.symbol+':'" />
                    <JetInput id="deposit" type="number" min="0" placeholder="0"
                        class="text-right" v-model="form.deposit" autocomplete="off"
                    />
                    <JetInputError :message="form.errors.deposit" />
                </div>
                <div class="col-span-1">
                    <JetLabel for="mode" value="Mode de paiement:" />
                    <select v-model="form.mode" class="form-input w-full uppercase">
                        <option value="null" disabled selected hidden>Choisissez le mode de paiement</option>
                        <option v-for="(mode, index) in modes" :key="index" :value="mode['value']">
                            {{ mode['description'] }}
                        </option>
                    </select>
                    <JetInputError :message="form.errors.mode" />
                </div>
            </div>
            <div class="grid grid-cols-2 gap-4 my-2">
                <div class="col-span-1">
                    <JetLabel for="description" value="Description du colis:" />
                    <textarea class="form-input w-full" v-model="form.description"
                        id="description" cols="30" rows="6" maxlength="150" placeholder="Entrer une brève description du colis ..."
                    ></textarea>
                    <JetInputError :message="form.errors.description" />
                </div>
                <div class="col-span-1">
                    <JetLabel for="address" value="Adresse de récupération du colis:" />
                    <textarea class="form-input w-full" v-model="form.address"
                        id="address" cols="30" rows="6" maxlength="150" placeholder="Entrer le lieu de récupération de colis ..."
                    ></textarea>
                    <JetInputError :message="form.errors.address" />
                </div>
            </div>
            <div v-if="! currentPackage" class="mt-2 border-l-4 bg-greendack-100 border-greendack-700 rounded-b text-greendack-700 p-1 shadow-md">
                <div class="flex items-center">
                    <i class="fad fa-info-circle text-3xl text-greendack-700 mr-4"></i>
                    <div>
                        <p class="font-bold text-greendack-700 text-sm">Vérification du colis</p>
                        <div class="block">
                            <label class="flex items-center">
                                <input type="checkbox" class="form-checkbox text-greendack-700" v-model="form.is_collected">
                                <span class="text-xs ml-2 font-semibold">
                                    Veuillez cocher cette case pour préciser que le colis a bien été collecté avec l'expéditeur.
                                </span>
                            </label>
                            <JetInputError :message="form.errors.is_collected" />
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <template #footer>
            <BotiButton @click="closeModal" text="Quitter" color="red">
                <template #icon><i class="fad fa-times-circle text-sm ml-1"></i></template>
            </BotiButton>

            <BotiButton :loader="form.processing" :disabled="form.processing"
                @click="manage" :text="currentPackage ? 'Modifier' : 'Enregistrer'"
                class="ml-2" :class="{ 'opacity-25': form.processing }" :color="currentPackage ? 'sky' : 'greendack'"
            >
                <svg v-if="form.processing" class="animate-spin w-4 h-4 fill-current shrink-0" viewBox="0 0 16 16">
                    <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z"></path>
                </svg>
                <template #icon v-if="currentPackage">
                    <i class="fad fa-user-edit text-sm ml-1"></i>
                </template>
                <template #icon v-else>
                    <i class="fad fa-check text-sm ml-1"></i>
                </template>
            </BotiButton>
        </template>
    </JetDialogModal>
</template>

<script>
    import { defineComponent } from 'vue'
    import JetLabel from '@/Jetstream/Label'
    import JetInput from '@/Jetstream/Input'
    import BotiButton from '@/Components/Button'
    import Multiselect from '@vueform/multiselect'
    import JetInputError from '@/Jetstream/InputError'
    import CurrencyMixins from '@/Mixins/CurrencyMixins'
    import JetDialogModal from '@/Jetstream/DialogModal'
    import FetchUserMixins from "@/Mixins/FetchUserMixins"
    import JetLabelRequired from '@/Jetstream/LabelRequired'

    export default defineComponent({
        components: {
            JetLabel,
            JetInput,
            BotiButton,
            Multiselect,
            JetInputError,
            JetDialogModal,
            JetLabelRequired
        },

        mixins: [FetchUserMixins, CurrencyMixins],

        props: {
            modes: Array,
            travel: Object,
            currency: Object
        },

        data() {
            return {
                currentPackage: null,
                showModal: false,
                currentTab: 1,
                request: null,
                form: this.$inertia.form({
                    receiver_name: null,
                    receiver_phone: null,
                    receiver_dial_code: null,
                    receiver_country_code: null,
                    code: null,
                    amount: null,
                    discount_percent: 0,
                    deposit: null,
                    mode: null,
                    weight: null,
                    description: null,
                    address: null,
                    sender_name: null,
                    sender_email: null,
                    sender_phone: null,
                    sender_dial_code: null,
                    sender_country_code: null,
                    sender_id: null,
                    request_id: null,
                    is_collected: false,
                    files: []
                }),
            }
        },

        computed: {
            finalAmount() {
                const amount = Number(this.form.amount) || 0
                const discount = Math.min(100, Math.max(0, Number(this.form.discount_percent) || 0))

                return Math.round(amount * (1 - discount / 100) * 100) / 100
            },

            discountAmount() {
                const amount = Number(this.form.amount) || 0

                return Math.round((amount - this.finalAmount) * 100) / 100
            },
        },

        created () {
            var that = this

            this.emitter.on('showPackageModal', function (evt) {
                that.showPackageModal(evt)
            })

            this.emitter.on('showFromRequest', function (evt) {
                that.showFromRequest(evt)
            })
        },

        methods: {
            senderPhoneInput(number, phoneObject) {
                if(number !== undefined && phoneObject !== undefined) {
                    this.form.sender_phone =  number ? number : ''
                    this.form.sender_dial_code = phoneObject ? phoneObject.country.dialCode : null
                    this.form.sender_country_code = phoneObject ? phoneObject.country.iso2 : null
                }
            },

            receiverPhoneInput(number, phoneObject) {
                if(number !== undefined && phoneObject !== undefined) {
                    this.form.receiver_phone =  number ? number : ''
                    this.form.receiver_dial_code = phoneObject ? phoneObject.country.dialCode : null
                    this.form.receiver_country_code = phoneObject ? phoneObject.country.iso2 : null
                }
            },

            closeModal() {
                this.showModal = false
                this.form.clearErrors()
                this.form.reset()
            },

            showPackageModal(currentPackage) {
                this.request = null

                if (currentPackage === undefined) {
                    this.currentPackage = null
                    this.form.reset()
                    this.form.discount_percent = 0
                } else {
                    this.currentPackage = currentPackage

                    this.form.receiver_name = currentPackage.receiver_name
                    this.form.receiver_phone = currentPackage.receiver_phone
                    this.form.receiver_dial_code = currentPackage.receiver_dial_code
                    this.form.receiver_country_code = currentPackage.receiver_country_code
                    this.form.code = currentPackage.code
                    this.form.amount = currentPackage.amount
                    this.form.discount_percent = currentPackage.discount_percent ?? 0
                    this.form.weight = currentPackage.weight
                    this.form.description = currentPackage.description
                    this.form.address = currentPackage.address
                }

                this.showModal = true
            },

            showFromRequest(request) {
                this.currentPackage = null
                this.request = request
                this.form.request_id = request.id
                this.form.weight = request.weight_package
                this.form.description = request.description
                this.form.address = request.address

                if (request.user_id) {
                    this.currentTab = 1
                    this.form.sender_id = request.user_id
                } else {
                    this.currentTab = 2
                    this.form.sender_name = request.requester_name
                    this.form.sender_email = request.requester_email
                    this.form.sender_phone = request.phone
                    this.form.sender_dial_code = request.dial_code
                    this.form.sender_country_code = request.country_code
                }

                this.showModal = true
            },

            manage() {
                if (this.currentPackage) {
                    this.update()
                } else {
                    this.store()
                }
            },

            store() {
                this.form
                    .transform((data) => ({
                        ...data,
                        type: this.currentTab,
                    }))
                    .post(route('travels.packages.store', this.travel), {
                        preserveScroll: true,
                        onSuccess: () =>{
                            this.closeModal()
                            this.$swal.fire(
                                'Colis',
                                'Colis enregistré avec succès.',
                                'success'
                            )
                        },
                        onError: (error) => {
                            console.log(error)
                        }
                    })
            },

            update() {
                this.form
                    .transform((data) => ({
                        ...data,
                        type: this.currentTab,
                    }))
                    .put(route('travels.packages.update', {travel: this.travel, package: this.currentPackage}), {
                        preserveScroll: true,
                        onSuccess: () =>{
                            this.closeModal()
                            this.$swal.fire(
                                'Colis',
                                'Colis mis à jour avec succès.',
                                'success'
                            )
                        },
                    })
            },
        }
    })
</script>
